import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FiSearch,
  FiBriefcase,
  FiMapPin,
  FiUsers,
  FiDollarSign,
  FiArrowRight,
  FiArrowLeft,
  FiArrowUp,
  FiUploadCloud,
  FiCheckCircle,
  FiAlertCircle,
  FiFileText,
  FiX,
  FiBookOpen,
  FiClock,
  FiInfo,
  FiShare2,
  FiHeart,
  FiLayers,
  FiGlobe,
  FiZap,
  FiPrinter,
  FiAward,
} from "react-icons/fi";
import { GiTakeMyMoney } from "react-icons/gi";
import Button from "../../../global-components/Button/Button";
import Loading from "../../../global-components/Loading/Loading";
import {
  fetchApprovedJobs,
  fetchJobById,
  submitApplication,
  parseResume,
} from "../recruitmentAPI";
import gmsLogo from "../../../Logo/GMS.png";
import ldsLogo from "../../../Logo/LDS.png";
import visoleLogo from "../../../Logo/viSole.png";
import "./JobOpenings.css";

/* ─────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────── */
const firstValue = (...values) =>
  values.find((v) => typeof v === "string" && v.trim())?.trim() || "";

const getResumeContactData = (data) => {
  const candidate = data?.candidate || data?.Candidate || data || {};
  const fullName = firstValue(
    candidate.name,
    candidate.fullName,
    candidate.FullName,
    candidate.Name
  ) || `${firstValue(candidate.first_name, candidate.firstName, candidate.FirstName)} ${firstValue(candidate.last_name, candidate.lastName, candidate.LastName)}`.trim();
  const nameParts = fullName.split(/\s+/).filter(Boolean);
  return {
    firstName: firstValue(candidate.first_name, candidate.firstName, candidate.FirstName, nameParts[0]),
    lastName: firstValue(candidate.last_name, candidate.lastName, candidate.LastName, nameParts.slice(1).join(" ")),
    fullName,
    email: firstValue(candidate.email, candidate.Email),
    phone: firstValue(candidate.phone, candidate.Phone, candidate.mobile, candidate.Mobile),
  };
};

const formatPKR = (val) => {
  const n = parseInt(String(val).replace(/\D/g, ""), 10);
  if (!n || isNaN(n)) return "";
  return `PKR ${n.toLocaleString("en-PK")} / month`;
};

const draftKey = (jobId) => `careers_draft_${jobId}`;

/* Relative time: "3 days ago", "just now", etc. */
const relativeTime = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  if (isNaN(diff)) return null;
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Posted today";
  if (days === 1) return "Posted yesterday";
  if (days < 30) return `Posted ${days} days ago`;
  const months = Math.floor(days / 30);
  return `Posted ${months} month${months > 1 ? "s" : ""} ago`;
};

/* Form steps definition */
const FORM_STEPS = [
  { num: 1, label: "Resume" },
  { num: 2, label: "Personal" },
  { num: 3, label: "Professional" },
  { num: 4, label: "Education" },
];

const TEXTAREA_LIMITS = { Skills: 300, CoverLetter: 2000 };

/* ─────────────────────────────────────────────────
   SKELETON CARD
───────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="careers-card careers-skeleton-card" aria-hidden="true">
    <div className="careers-card-top">
      <div className="careers-sk careers-sk--title" />
      <div className="careers-sk careers-sk--tag" />
      <div className="careers-sk careers-sk--company" />
      <div className="careers-sk careers-sk--chips" />
      <div className="careers-sk careers-sk--desc" />
      <div className="careers-sk careers-sk--desc-short" />
    </div>
    <div className="careers-card-footer">
      <div className="careers-sk careers-sk--btn" />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────
   TOAST
───────────────────────────────────────────────── */
const Toast = ({ message, visible }) => (
  <div className={`careers-toast ${visible ? "careers-toast--visible" : ""}`}>
    <FiCheckCircle className="careers-toast-icon" />
    {message}
  </div>
);

/* ─────────────────────────────────────────────────
   FORM PROGRESS BAR
───────────────────────────────────────────────── */
const FormProgress = ({ currentStep }) => (
  <div className="careers-progress" aria-label="Application progress">
    {FORM_STEPS.map((step, i) => {
      const isDone = currentStep > step.num;
      const isActive = currentStep === step.num;
      return (
        <React.Fragment key={step.num}>
          <div
            className={`careers-progress-step ${isDone ? "careers-progress-step--done" : ""} ${isActive ? "careers-progress-step--active" : ""}`}
          >
            <div className="careers-progress-circle">
              {isDone ? <FiCheckCircle /> : step.num}
            </div>
            <span className="careers-progress-label">{step.label}</span>
          </div>
          {i < FORM_STEPS.length - 1 && (
            <div className={`careers-progress-line ${isDone ? "careers-progress-line--done" : ""}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────────────
   SCROLL-TO-TOP BUTTON
───────────────────────────────────────────────── */
const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = document.querySelector(".app-content-scroll");
    if (!el) return;
    const onScroll = () => setVisible(el.scrollTop > 300);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  const handleClick = () => {
    const el = document.querySelector(".app-content-scroll");
    if (el) el.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <button
      className={`careers-scroll-top ${visible ? "careers-scroll-top--visible" : ""}`}
      onClick={handleClick}
      aria-label="Scroll to top"
      title="Back to top"
    >
      <FiArrowUp />
    </button>
  );
};

/* ─────────────────────────────────────────────────
   CHAR COUNTER
───────────────────────────────────────────────── */
const CharCounter = ({ value, max }) => {
  const len = (value || "").length;
  const pct = len / max;
  return (
    <span className={`careers-char-counter ${pct >= 1 ? "careers-char-counter--over" : pct >= 0.8 ? "careers-char-counter--warn" : ""}`}>
      {len} / {max}
    </span>
  );
};

/* ─────────────────────────────────────────────────
   NO JOBS EMPTY STATE
───────────────────────────────────────────────── */
const NoJobsState = ({ hasFilters, onClear }) => (
  <div className="careers-empty-card careers-empty-card--illustrated">
    <div className="careers-empty-illustration" aria-hidden="true">
      <div className="careers-empty-circle careers-empty-circle--lg" />
      <div className="careers-empty-circle careers-empty-circle--sm" />
      <FiHeart className="careers-empty-heart" />
      <FiBriefcase className="careers-empty-briefcase" />
    </div>
    <h2 className="careers-empty-title">
      {hasFilters ? "No Matching Positions" : "No Open Positions Right Now"}
    </h2>
    <p className="careers-empty-desc">
      {hasFilters
        ? "We couldn't find any positions matching your current filters. Try adjusting your search or removing a filter."
        : "We don't have any open roles at the moment. Please check back soon — we're always growing!"}
    </p>
    {hasFilters && (
      <Button variant="outline" onClick={onClear}>
        Clear All Filters
      </Button>
    )}
  </div>
);

/* ─────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────── */
const JobOpenings = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();

  const [jobsData, setJobsData] = useState([]);
  const [currentJob, setCurrentJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Search & Filter */
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("All");

  /* Toast */
  const [toastMsg, setToastMsg] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = useRef(null);

  /* Mobile overview panel toggle */
  const [showMobileOverview, setShowMobileOverview] = useState(false);

  /* Form step tracking & inline field validation errors */
  const [activeFormStep, setActiveFormStep] = useState(1);
  const [fieldErrors, setFieldErrors] = useState({});

  const EMPTY_FORM = {
    FirstName: "",
    LastName: "",
    FullName: "",
    Email: "",
    Phone: "",
    ExperienceYears: "",
    CurrentCompany: "",
    CurrentDesignation: "",
    CurrentSalary: "",
    ExpectedSalary: "",
    Qualification: "",
    Skills: "",
    CoverLetter: "",
    resume: null,
  };

  const loadDraft = useCallback((id) => {
    try {
      const raw = localStorage.getItem(draftKey(id));
      if (!raw) return EMPTY_FORM;
      const parsed = JSON.parse(raw);
      return { ...EMPTY_FORM, ...parsed, resume: null };
    } catch {
      return EMPTY_FORM;
    }
  }, []);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [processingResume, setProcessingResume] = useState(false);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState({});
  const [parseError, setParseError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  /* Auto-save draft */
  useEffect(() => {
    if (!jobId) return;
    const { resume, ...saveable } = formData;
    try { localStorage.setItem(draftKey(jobId), JSON.stringify(saveable)); }
    catch { /* storage full */ }
  }, [formData, jobId]);

  /* Track active form step based on filled fields */
  useEffect(() => {
    if (!jobId) return;
    if (formData.Skills || formData.Qualification) { setActiveFormStep(4); return; }
    if (formData.CurrentCompany || formData.ExperienceYears) { setActiveFormStep(3); return; }
    if (formData.FirstName || formData.Email) { setActiveFormStep(2); return; }
    setActiveFormStep(1);
  }, [formData, jobId]);

  /* Load data */
  useEffect(() => {
    const loadJobs = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchApprovedJobs();
        if (result.success) {
          setJobsData(result.data || []);
        } else {
          throw new Error(result.error || "Failed to fetch open positions");
        }
      } catch (err) {
        setError(err.message || "Unable to connect to the database. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    const loadCurrentJob = async (id) => {
      try {
        setLoading(true);
        setError(null);
        const cached = jobsData.find((j) => parseInt(j.id) === parseInt(id));
        if (cached) { setCurrentJob(cached); setLoading(false); return; }
        const result = await fetchJobById(id);
        if (result.success && result.data) {
          setCurrentJob(result.data);
        } else {
          throw new Error(result.error || "Job position not found.");
        }
      } catch (err) {
        setError(err.message || "Failed to load job details.");
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      loadCurrentJob(jobId);
      setFormData(loadDraft(jobId));
    } else {
      loadJobs();
    }
  }, [jobId]);

  /* Derived */
  const departments = useMemo(() => {
    const set = new Set();
    jobsData.forEach((j) => { if (j.company) set.add(j.company); });
    return Array.from(set);
  }, [jobsData]);

  const deptCounts = useMemo(() => {
    const map = {};
    jobsData.forEach((j) => { if (j.company) map[j.company] = (map[j.company] || 0) + 1; });
    return map;
  }, [jobsData]);

  const filteredJobs = useMemo(() => {
    return jobsData.filter((job) => {
      const matchSearch =
        !searchQuery ||
        job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchDept = selectedDepartment === "All" || job.company === selectedDepartment;
      return matchSearch && matchDept;
    });
  }, [jobsData, searchQuery, selectedDepartment]);

  const hasActiveFilters = searchQuery || selectedDepartment !== "All";

  const clearFilters = () => { setSearchQuery(""); setSelectedDepartment("All"); };

  /* Toast */
  const showToast = (msg) => {
    clearTimeout(toastTimer.current);
    setToastMsg(msg);
    setToastVisible(true);
    toastTimer.current = setTimeout(() => setToastVisible(false), 3000);
  };

  /* Share */
  const handleShareJob = (e, id) => {
    e.stopPropagation();
    const url = `${window.location.origin}/${id}?view=form`;
    navigator.clipboard.writeText(url)
      .then(() => showToast("Job link copied to clipboard!"))
      .catch(() => showToast("Unable to copy. Please copy the URL manually."));
  };

  /* Form */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const limit = TEXTAREA_LIMITS[name];
    if (limit && value.length > limit) return; // enforce char limit
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear inline error on change
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (autoFilledFields[name]) {
      setAutoFilledFields((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) { setParseError("Please upload a valid PDF file."); return; }
    if (file.size > 5 * 1024 * 1024) { setParseError("File size exceeds 5MB limit."); return; }

    setFormData((prev) => ({ ...prev, resume: file }));
    setFieldErrors((prev) => ({ ...prev, resume: "" }));
    setParseError(null);
    setParseSuccess(false);

    try {
      setProcessingResume(true);
      const result = await parseResume(file, jobId);
      if (result.success && result.data) {
        const contactData = getResumeContactData(result.data);
        const coverLetter = result.data?.cover_letter?.text || result.data?.coverLetter || "";
        setFormData((prev) => ({
          ...prev,
          FirstName: contactData.firstName || prev.FirstName,
          LastName: contactData.lastName || prev.LastName,
          FullName: contactData.fullName || prev.FullName,
          Email: contactData.email || prev.Email,
          Phone: contactData.phone || prev.Phone,
          CoverLetter: coverLetter || prev.CoverLetter,
          Qualification: result.data?.candidate?.qualification || result.data?.candidate?.Qualification || prev.Qualification,
          Skills: result.data?.candidate?.skills || result.data?.candidate?.Skills || prev.Skills,
          CurrentCompany: result.data?.candidate?.current_company || result.data?.candidate?.currentCompany || prev.CurrentCompany,
          CurrentDesignation: result.data?.candidate?.current_designation || result.data?.candidate?.currentDesignation || prev.CurrentDesignation,
        }));
        setParseSuccess(true);
        const fieldsFilled = {};
        if (contactData.firstName) fieldsFilled.FirstName = true;
        if (contactData.lastName) fieldsFilled.LastName = true;
        if (contactData.fullName) fieldsFilled.FullName = true;
        if (contactData.email) fieldsFilled.Email = true;
        if (contactData.phone) fieldsFilled.Phone = true;
        if (coverLetter) fieldsFilled.CoverLetter = true;
        if (result.data?.candidate?.qualification || result.data?.candidate?.Qualification) fieldsFilled.Qualification = true;
        if (result.data?.candidate?.skills || result.data?.candidate?.Skills) fieldsFilled.Skills = true;
        if (result.data?.candidate?.current_company || result.data?.candidate?.currentCompany) fieldsFilled.CurrentCompany = true;
        if (result.data?.candidate?.current_designation || result.data?.candidate?.currentDesignation) fieldsFilled.CurrentDesignation = true;
        setAutoFilledFields(fieldsFilled);
        showToast("✨ Resume details auto-filled!");
      } else {
        setParseError(result.error || "Unable to parse resume. Please fill fields manually.");
      }
    } catch (err) {
      console.warn("Resume parsing skipped:", err);
      setParseError("Unable to extract details. Please fill fields manually.");
    } finally {
      setProcessingResume(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFileUpload(e.dataTransfer.files[0]);
  };

  const isStepCompleted = (stepNum) => {
    if (stepNum === 1) return !!formData.resume;
    if (stepNum === 2) {
      return !!formData.FirstName?.trim() &&
        !!formData.LastName?.trim() &&
        !!formData.Email?.trim() &&
        /^[^s@]+@[^s@]+\.[^\s@]+$/.test(formData.Email) &&
        !!formData.Phone?.trim();
    }
    if (stepNum === 3) {
      return !!formData.CurrentCompany?.trim() &&
        !!formData.CurrentDesignation?.trim() &&
        (!!formData.ExperienceYears || formData.ExperienceYears === 0) &&
        (!!formData.CurrentSalary || formData.CurrentSalary === 0) &&
        (!!formData.ExpectedSalary || formData.ExpectedSalary === 0);
    }
    if (stepNum === 4) {
      return !!formData.Qualification?.trim();
    }
    return false;
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.resume) {
      errors.resume = "Please attach your resume (PDF).";
    }
    if (!formData.FirstName?.trim()) {
      errors.FirstName = "First name is required.";
    }
    if (!formData.LastName?.trim()) {
      errors.LastName = "Last name is required.";
    }
    if (!formData.Email?.trim()) {
      errors.Email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      errors.Email = "Please enter a valid email address.";
    }
    if (!formData.Phone?.trim()) {
      errors.Phone = "Phone number is required.";
    }
    if (!formData.CurrentCompany?.trim()) {
      errors.CurrentCompany = "Current company is required.";
    }
    if (!formData.CurrentDesignation?.trim()) {
      errors.CurrentDesignation = "Current designation is required.";
    }
    if (!formData.ExperienceYears && formData.ExperienceYears !== 0) {
      errors.ExperienceYears = "Experience is required.";
    }
    if (!formData.CurrentSalary && formData.CurrentSalary !== 0) {
      errors.CurrentSalary = "Current salary is required.";
    }
    if (!formData.ExpectedSalary && formData.ExpectedSalary !== 0) {
      errors.ExpectedSalary = "Expected salary is required.";
    }
    if (!formData.Qualification?.trim()) {
      errors.Qualification = "Qualification is required.";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const errors = validateForm();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitError("Please fill in all required fields highlighted below.");
      const firstErrorKey = Object.keys(errors)[0];
      const targetElement = document.getElementById(firstErrorKey) || (firstErrorKey === "resume" ? document.getElementById("step-1") : null);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
        if (targetElement.focus) targetElement.focus();
      }
      return;
    }

    try {
      setSubmitting(true);
      const result = await submitApplication(jobId, formData);
      if (result.success) {
        localStorage.removeItem(draftKey(jobId));
        setShowSuccessModal(true);
      } else {
        throw new Error(result.error || "Failed to submit application.");
      }
    } catch (err) {
      setSubmitError(err.message || "An error occurred while submitting your application.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ═══════════════════════════════════════════════
     LOADING — Skeletons
  ═══════════════════════════════════════════════ */
  if (loading && !jobId) {
    return (
      <div className="careers-container">
        <section className="careers-hero">
          <div className="careers-sk careers-sk--hero-badge" />
          <div className="careers-sk careers-sk--hero-title" />
          <div className="careers-sk careers-sk--hero-sub" />
          <div className="careers-sk careers-sk--filter-bar" />
        </section>
        <div className="careers-results-bar">
          <div className="careers-sk careers-sk--results-text" />
        </div>
        <div className="careers-grid">
          {[1, 2, 3, 4, 5, 6].map((n) => <SkeletonCard key={n} />)}
        </div>
        <ScrollToTop />
      </div>
    );
  }

  if (loading && jobId) {
    return (
      <div className="careers-container">
        <div className="careers-sk careers-sk--back-btn" />
        <div className="careers-form-layout">
          <div className="careers-sk careers-sk--overview-card" />
          <div className="careers-sk careers-sk--form-area" />
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════
     ERROR
  ═══════════════════════════════════════════════ */
  if (error && !currentJob && (!jobsData || jobsData.length === 0)) {
    return (
      <div className="careers-container">
        <div className="careers-empty-card">
          <FiAlertCircle className="careers-empty-icon careers-empty-icon--danger" />
          <h2 className="careers-empty-title">Database Connection Error</h2>
          <p className="careers-empty-desc">{error}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>Retry Connection</Button>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════
     APPLICATION FORM VIEW
  ═══════════════════════════════════════════════ */
  if (jobId && currentJob) {
    return (
      <div className="careers-container">
        {processingResume && (
          <Loading
            variant="card"
            overlay={true}
            className="fixed-loader"
            message="Processing..."
          />
        )}
        <Toast message={toastMsg} visible={toastVisible} />
        <ScrollToTop />

        <div className="careers-form-topbar">
          <Button variant="ghost" icon={<FiArrowLeft />} onClick={() => navigate("/")}>
            Back to Available Jobs
          </Button>
        </div>

        {/* Mobile: collapsible job overview banner */}
        <div className="careers-mobile-overview-toggle" onClick={() => setShowMobileOverview((p) => !p)}>
          <div className="careers-mobile-overview-info">
            <span className="careers-tag careers-tag--primary">{currentJob.company}</span>
            <span className="careers-mobile-overview-title">{currentJob.title}</span>
          </div>
          <span className="careers-mobile-overview-chevron">{showMobileOverview ? "▲" : "▼"}</span>
        </div>
        {showMobileOverview && (
          <div className="careers-mobile-overview-body">
            <div className="careers-job-overview-meta-list">
              <div className="careers-job-overview-meta-item">
                <div className="careers-job-overview-meta-icon"><FiMapPin /></div>
                <div>
                  <div className="careers-job-overview-meta-label">Location</div>
                  <div className="careers-job-overview-meta-value">{currentJob.location}</div>
                </div>
              </div>
              {currentJob.experience && (
                <div className="careers-job-overview-meta-item">
                  <div className="careers-job-overview-meta-icon"><FiBriefcase /></div>
                  <div>
                    <div className="careers-job-overview-meta-label">Experience</div>
                    <div className="careers-job-overview-meta-value">{currentJob.experience}</div>
                  </div>
                </div>
              )}
              {currentJob.salary && (
                <div className="careers-job-overview-meta-item">
                  <div className="careers-job-overview-meta-icon"><GiTakeMyMoney /></div>
                  <div>
                    <div className="careers-job-overview-meta-label">Compensation</div>
                    <div className="careers-job-overview-meta-value">{currentJob.salary}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress bar */}
        <FormProgress currentStep={activeFormStep} />

        <div className="careers-form-layout">
          {/* Left: Sticky Overview */}
          <aside className="careers-job-overview">
            <div className="careers-job-overview-header">
              <span className="careers-tag careers-tag--primary">{currentJob.company}</span>
              {currentJob.type ? (
                <span className="careers-tag careers-tag--accent">{currentJob.type}</span>
              ) : null}
            </div>
            <h1 className="careers-job-overview-title">{currentJob.title}</h1>
            <div className="careers-job-overview-meta-list">
              <div className="careers-job-overview-meta-item">
                <div className="careers-job-overview-meta-icon"><FiMapPin /></div>
                <div>
                  <div className="careers-job-overview-meta-label">Location</div>
                  <div className="careers-job-overview-meta-value">{currentJob.location}</div>
                </div>
              </div>
              <div className="careers-job-overview-meta-item">
                <div className="careers-job-overview-meta-icon"><FiBriefcase /></div>
                <div>
                  <div className="careers-job-overview-meta-label">Experience Required</div>
                  <div className="careers-job-overview-meta-value">{currentJob.experience}</div>
                </div>
              </div>
              {currentJob.qualification && (
                <div className="careers-job-overview-meta-item">
                  <div className="careers-job-overview-meta-icon"><FiBookOpen /></div>
                  <div>
                    <div className="careers-job-overview-meta-label">Qualification</div>
                    <div className="careers-job-overview-meta-value">{currentJob.qualification}</div>
                  </div>
                </div>
              )}
              {currentJob.vacancies && (
                <div className="careers-job-overview-meta-item">
                  <div className="careers-job-overview-meta-icon"><FiUsers /></div>
                  <div>
                    <div className="careers-job-overview-meta-label">Vacancies</div>
                    <div className="careers-job-overview-meta-value">{currentJob.vacancies} Position(s)</div>
                  </div>
                </div>
              )}
              {currentJob.salary && (
                <div className="careers-job-overview-meta-item">
                  <div className="careers-job-overview-meta-icon"><GiTakeMyMoney /></div>
                  <div>
                    <div className="careers-job-overview-meta-label">Compensation</div>
                    <div className="careers-job-overview-meta-value">{currentJob.salary}</div>
                  </div>
                </div>
              )}
            </div>
            {currentJob.description && (
              <div className="careers-job-overview-description">
                <div className="careers-job-overview-desc-heading">Job Description</div>
                <div className="careers-job-overview-desc-content">{currentJob.description}</div>
              </div>
            )}
          </aside>

          {/* Right: Application Form */}
          <main className="careers-form-main">
            {/* Draft notice */}
            {(formData.FirstName || formData.Email) && (
              <div className="careers-notice careers-notice--info careers-draft-notice">
                <FiClock className="careers-notice-icon" />
                <span>Your previous draft has been restored. Continue where you left off.</span>
                <button
                  className="careers-draft-clear-btn"
                  type="button"
                  onClick={() => { setFormData(EMPTY_FORM); setFieldErrors({}); localStorage.removeItem(draftKey(jobId)); }}
                >
                  Clear Draft
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="careers-form" noValidate>
              {/* STEP 1 — Resume */}
              <div className="careers-form-card" id="step-1">
                <div className="careers-form-card-header">
                  <div className={`careers-form-step-badge ${isStepCompleted(1) ? "careers-form-step-badge--completed" : ""}`}>{isStepCompleted(1) ? "✓" : "1"}</div>
                  <div>
                    <h2 className="careers-form-card-title">Resume &amp; Smart Auto-Fill</h2>
                    <p className="careers-form-card-subtitle">Upload your PDF resume. Our system will extract and pre-fill your information.</p>
                  </div>
                </div>

                <input ref={fileInputRef} type="file" className="careers-dropzone-input" accept=".pdf"
                  disabled={processingResume}
                  onChange={(e) => handleFileUpload(e.target.files?.[0])} />

                {!formData.resume && (
                  <div
                    className={`careers-dropzone ${isDragOver ? "careers-dropzone--active" : ""} ${fieldErrors.resume ? "careers-dropzone--error" : ""} ${processingResume ? "careers-dropzone--disabled" : ""}`}
                    onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                    onClick={() => !processingResume && fileInputRef.current?.click()}
                  >
                    <FiUploadCloud className="careers-dropzone-icon" />
                    <div className="careers-dropzone-title">Click to browse or drag &amp; drop</div>
                    <div className="careers-dropzone-hint">PDF format only — Max 5MB</div>
                  </div>
                )}

                {fieldErrors.resume && (
                  <span className="careers-field-error-text">
                    <FiAlertCircle /> {fieldErrors.resume}
                  </span>
                )}

                {formData.resume && (
                  <div className="careers-file-card">
                    <div className="careers-file-card-left">
                      <div className="careers-file-card-icon-wrap">
                        <FiFileText className="careers-file-card-icon" />
                      </div>
                      <div className="careers-file-card-details">
                        <div className="careers-file-card-name">{formData.resume.name}</div>
                        <div className="careers-file-card-meta">
                          <span>{(formData.resume.size / 1024).toFixed(0)} KB</span>
                          <span className="careers-file-card-divider">&bull;</span>
                          <span className="careers-file-card-tag">PDF Attached</span>
                        </div>
                      </div>
                    </div>
                    <div className="careers-file-card-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={processingResume}
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        Replace
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        icon={<FiX />}
                        disabled={processingResume}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData((p) => ({ ...p, resume: null }));
                          setParseSuccess(false);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
                {processingResume && (
                  <div className="careers-notice careers-notice--info">
                    <FiClock className="careers-notice-icon" />
                    <span>Extracting details from your resume…</span>
                  </div>
                )}
                {parseSuccess && (
                  <div className="careers-notice careers-notice--success">
                    <FiCheckCircle className="careers-notice-icon" />
                    <span>Information successfully extracted and auto-filled below.</span>
                  </div>
                )}
                {parseError && (
                  <div className="careers-notice careers-notice--warning">
                    <FiAlertCircle className="careers-notice-icon" />
                    <span>{parseError}</span>
                  </div>
                )}
              </div>

              {/* STEP 2 — Personal */}
              <div className="careers-form-card" id="step-2">
                <div className="careers-form-card-header">
                  <div className={`careers-form-step-badge ${isStepCompleted(2) ? "careers-form-step-badge--completed" : ""}`}>{isStepCompleted(2) ? "✓" : "2"}</div>
                  <div>
                    <h2 className="careers-form-card-title">Personal Information</h2>
                    <p className="careers-form-card-subtitle">Your primary contact details for this application.</p>
                  </div>
                </div>
                <div className="careers-form-grid-2">
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="FirstName">
                      First Name <span className="careers-field-req">*</span>
                      {autoFilledFields.FirstName && <span > Auto-filled</span>}
                    </label>
                    <input id="FirstName" name="FirstName" type="text"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.FirstName ? "careers-input--error" : ""} ${processingResume && !formData.FirstName ? "careers-input--loading" : ""} ${autoFilledFields.FirstName ? "careers-input--autofilled" : ""}`}
                      value={formData.FirstName} onChange={handleInputChange} placeholder="e.g. Muhammad" required />
                    {fieldErrors.FirstName && <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.FirstName}</span>}
                  </div>
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="LastName">
                      Last Name <span className="careers-field-req">*</span>
                      {autoFilledFields.LastName && <span > Auto-filled</span>}
                    </label>
                    <input id="LastName" name="LastName" type="text"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.LastName ? "careers-input--error" : ""} ${processingResume && !formData.LastName ? "careers-input--loading" : ""} ${autoFilledFields.LastName ? "careers-input--autofilled" : ""}`}
                      value={formData.LastName} onChange={handleInputChange} placeholder="e.g. Ali" required />
                    {fieldErrors.LastName && <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.LastName}</span>}
                  </div>
                </div>
                <div className="careers-form-grid-2 careers-form-field-row">
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="Email">
                      Email Address <span className="careers-field-req">*</span>
                      {autoFilledFields.Email && <span >Auto-filled</span>}
                    </label>
                    <input id="Email" name="Email" type="email"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.Email ? "careers-input--error" : ""} ${processingResume && !formData.Email ? "careers-input--loading" : ""} ${autoFilledFields.Email ? "careers-input--autofilled" : ""}`}
                      value={formData.Email} onChange={handleInputChange} placeholder="name@example.com" required />
                    {fieldErrors.Email && <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.Email}</span>}
                  </div>
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="Phone">
                      Phone Number <span className="careers-field-req">*</span>
                      {autoFilledFields.Phone && <span >Auto-filled</span>}
                    </label>
                    <input id="Phone" name="Phone" type="tel"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.Phone ? "careers-input--error" : ""} ${processingResume && !formData.Phone ? "careers-input--loading" : ""} ${autoFilledFields.Phone ? "careers-input--autofilled" : ""}`}
                      value={formData.Phone} onChange={handleInputChange} placeholder="e.g., +923001234567 or 03001234567"
                      required />
                    {fieldErrors.Phone && (
                      <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.Phone}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* STEP 3 — Professional */}
              <div className="careers-form-card" id="step-3">
                <div className="careers-form-card-header">
                  <div className={`careers-form-step-badge ${isStepCompleted(3) ? "careers-form-step-badge--completed" : ""}`}>{isStepCompleted(3) ? "✓" : "3"}</div>
                  <div>
                    <h2 className="careers-form-card-title">Professional Background &amp; Salary</h2>
                    <p className="careers-form-card-subtitle">Your current role and salary expectations.</p>
                  </div>
                </div>
                <div className="careers-form-grid-2">
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="CurrentCompany">
                      Current Company <span className="careers-field-req">*</span>
                      {autoFilledFields.CurrentCompany && <span > Auto-filled</span>}
                    </label>
                    <input id="CurrentCompany" name="CurrentCompany" type="text"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.CurrentCompany ? "careers-input--error" : ""} ${processingResume && !formData.CurrentCompany ? "careers-input--loading" : ""} ${autoFilledFields.CurrentCompany ? "careers-input--autofilled" : ""}`}
                      value={formData.CurrentCompany} onChange={handleInputChange} placeholder="Current or latest company" required />
                    {fieldErrors.CurrentCompany && <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.CurrentCompany}</span>}
                  </div>
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="CurrentDesignation">
                      Current Designation <span className="careers-field-req">*</span>
                      {autoFilledFields.CurrentDesignation && <span > Auto-filled</span>}
                    </label>
                    <input id="CurrentDesignation" name="CurrentDesignation" type="text"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.CurrentDesignation ? "careers-input--error" : ""} ${processingResume && !formData.CurrentDesignation ? "careers-input--loading" : ""} ${autoFilledFields.CurrentDesignation ? "careers-input--autofilled" : ""}`}
                      value={formData.CurrentDesignation} onChange={handleInputChange} placeholder="e.g. Software Engineer" required />
                    {fieldErrors.CurrentDesignation && <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.CurrentDesignation}</span>}
                  </div>
                </div>
                <div className="careers-form-grid-3 careers-form-field-row">
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="ExperienceYears">
                      Experience (Years) <span className="careers-field-req">*</span>
                      {autoFilledFields.ExperienceYears && <span > Auto-filled</span>}
                    </label>
                    <input id="ExperienceYears" name="ExperienceYears" type="number" min="0" step="0.5"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.ExperienceYears ? "careers-input--error" : ""} ${processingResume && !formData.ExperienceYears ? "careers-input--loading" : ""} ${autoFilledFields.ExperienceYears ? "careers-input--autofilled" : ""}`}
                      value={formData.ExperienceYears} onChange={handleInputChange} placeholder="e.g. 3" required />
                    {fieldErrors.ExperienceYears && <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.ExperienceYears}</span>}
                  </div>
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="CurrentSalary">
                      Current Salary <span className="careers-field-req">*</span>
                      {autoFilledFields.CurrentSalary && <span > Auto-filled</span>}
                    </label>
                    <input id="CurrentSalary" name="CurrentSalary" type="number" min="0"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.CurrentSalary ? "careers-input--error" : ""} ${processingResume && !formData.CurrentSalary ? "careers-input--loading" : ""} ${autoFilledFields.CurrentSalary ? "careers-input--autofilled" : ""}`}
                      value={formData.CurrentSalary} onChange={handleInputChange} placeholder="e.g. 120000" required />
                    {fieldErrors.CurrentSalary ? (
                      <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.CurrentSalary}</span>
                    ) : formData.CurrentSalary ? (
                      <span className="careers-field-hint">{formatPKR(formData.CurrentSalary)}</span>
                    ) : null}
                  </div>
                  <div className="careers-form-field">
                    <label className="careers-field-label" htmlFor="ExpectedSalary">
                      Expected Salary <span className="careers-field-req">*</span>
                      {autoFilledFields.ExpectedSalary && <span > Auto-filled</span>}
                    </label>
                    <input id="ExpectedSalary" name="ExpectedSalary" type="number" min="0"
                      disabled={processingResume}
                      className={`careers-input ${fieldErrors.ExpectedSalary ? "careers-input--error" : ""} ${processingResume && !formData.ExpectedSalary ? "careers-input--loading" : ""} ${autoFilledFields.ExpectedSalary ? "careers-input--autofilled" : ""}`}
                      value={formData.ExpectedSalary} onChange={handleInputChange} placeholder="e.g. 150000" required />
                    {fieldErrors.ExpectedSalary ? (
                      <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.ExpectedSalary}</span>
                    ) : formData.ExpectedSalary ? (
                      <span className="careers-field-hint">{formatPKR(formData.ExpectedSalary)}</span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* STEP 4 — Education */}
              <div className="careers-form-card" id="step-4">
                <div className="careers-form-card-header">
                  <div className={`careers-form-step-badge ${isStepCompleted(4) ? "careers-form-step-badge--completed" : ""}`}>{isStepCompleted(4) ? "✓" : "4"}</div>
                  <div>
                    <h2 className="careers-form-card-title">Education &amp; Key Skills</h2>
                    <p className="careers-form-card-subtitle">Your qualification and competencies.</p>
                  </div>
                </div>
                <div className="careers-form-field">
                  <label className="careers-field-label" htmlFor="Qualification">
                    Qualification <span className="careers-field-req">*</span>
                    {autoFilledFields.Qualification && <span className=""> Auto-filled</span>}
                  </label>
                  <input id="Qualification" name="Qualification" type="text"
                    disabled={processingResume}
                    className={`careers-input ${fieldErrors.Qualification ? "careers-input--error" : ""} ${processingResume && !formData.Qualification ? "careers-input--loading" : ""} ${autoFilledFields.Qualification ? "careers-input--autofilled" : ""}`}
                    value={formData.Qualification} onChange={handleInputChange} placeholder="e.g. BS Computer Science / MBA" required />
                  {fieldErrors.Qualification && <span className="careers-field-error-text"><FiAlertCircle /> {fieldErrors.Qualification}</span>}
                </div>
                <div className="careers-form-field careers-form-field-row">
                  <div className="careers-textarea-header">
                    <label className="careers-field-label" htmlFor="Skills">
                      Skills <span className="careers-field-opt">(Optional)</span>
                      {autoFilledFields.Skills && <span className="">  Auto-filled</span>}
                    </label>
                    <CharCounter value={formData.Skills} max={TEXTAREA_LIMITS.Skills} />
                  </div>
                  <textarea id="Skills" name="Skills" className={`careers-textarea ${processingResume && !formData.Skills ? "careers-textarea--loading" : ""} ${autoFilledFields.Skills ? "careers-textarea--autofilled" : ""}`} rows="3"
                    disabled={processingResume}
                    value={formData.Skills} onChange={handleInputChange}
                    placeholder="e.g. React, Node.js, SQL, Problem Solving (comma separated)" />
                </div>
              </div>

              {submitError && (
                <div className="careers-notice careers-notice--danger">
                  <FiAlertCircle className="careers-notice-icon" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="careers-form-actions">
                <Button variant="ghost" type="button" disabled={processingResume} onClick={() => navigate("/")}>Cancel</Button>
                <Button variant="primary" type="submit" size="lg" disabled={processingResume || submitting} isLoading={submitting}>
                  Submit Application
                </Button>
              </div>
            </form>
          </main>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="careers-modal-backdrop">
            <div className="careers-modal-card">
              <div className="careers-modal-icon-wrap">
                <FiCheckCircle className="careers-modal-icon" />
              </div>
              <h2 className="careers-modal-title">Application Submitted!</h2>
              <p className="careers-modal-desc">
                Thank you for applying for <strong>{currentJob.title}</strong> at{" "}
                <strong>{currentJob.company}</strong>. We have received your profile and will be in touch.
              </p>
              <div className="careers-modal-timeline">
                <div className="careers-modal-step careers-modal-step--done">
                  <div className="careers-modal-step-number">✓</div>
                  <span>Application Received &amp; Recorded</span>
                </div>
                <div className="careers-modal-step">
                  <div className="careers-modal-step-number">2</div>
                  <span>Review &amp; Shortlisting</span>
                </div>
                <div className="careers-modal-step">
                  <div className="careers-modal-step-number">3</div>
                  <span>Interview Scheduling</span>
                </div>
              </div>
              <Button variant="primary" size="lg" onClick={() => { setShowSuccessModal(false); navigate("/"); }}>
                Back to Available Positions
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════
     JOB LISTING VIEW
  ═══════════════════════════════════════════════ */
  return (
    <div className="careers-container">
      <Toast message={toastMsg} visible={toastVisible} />
      <ScrollToTop />

      {/* ── Executive Corporate Hero Section ── */}
      <section className="careers-hero">
        {/* Top Status Badge */}
        <div className="careers-hero-badge">
          <span className="careers-hero-pulse-dot" />
          <span className="careers-hero-badge-text">
            Group Careers Portal &bull; Open Opportunities
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="careers-hero-title">
          Discover Your Next <span className="careers-hero-title-accent">Career Move</span>
        </h1>
        <p className="careers-hero-subtitle">
          Explore verified positions across Healthcare, Diagnostics, and Enterprise Technology divisions. Apply directly to our hiring managers.
        </p>

        {/* Unified Search & Filter Command Bar */}
        <div className="careers-filter-console">
          <div className="careers-search-wrapper">
            <FiSearch className="careers-search-icon" />
            <input
              type="text"
              className="careers-search-input"
              placeholder="Search by job title, skill, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="careers-search-clear" onClick={() => setSearchQuery("")} aria-label="Clear search">
                <FiX />
              </button>
            )}
          </div>

          <div className="careers-filter-console-divider" />

          <div className="careers-filter-select-wrapper">
            <FiLayers className="careers-filter-select-icon" />
            <select
              className="careers-filter-select"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              <option value="All">All Departments ({jobsData.length})</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept} ({deptCounts[dept] || 0})</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <button className="careers-filter-reset-btn" onClick={clearFilters} type="button">
              Reset
            </button>
          )}
        </div>

        {/* Interactive Department Filter Pills */}
        {departments.length > 0 && (
          <div className="careers-dept-chips">
            <button
              className={`careers-dept-chip ${selectedDepartment === "All" ? "careers-dept-chip--active" : ""}`}
              onClick={() => setSelectedDepartment("All")}
            >
              All Positions <span className="careers-dept-chip-count">{jobsData.length}</span>
            </button>
            {departments.map((dept) => (
              <button
                key={dept}
                className={`careers-dept-chip ${selectedDepartment === dept ? "careers-dept-chip--active" : ""}`}
                onClick={() => setSelectedDepartment(dept)}
              >
                {dept} <span className="careers-dept-chip-count">{deptCounts[dept] || 0}</span>
              </button>
            ))}
          </div>
        )}

        {/* Sleek Bottom Stat Ribbon */}
        <div className="careers-hero-ribbon">
          <div className="careers-hero-ribbon-item">
            <FiZap className="careers-hero-ribbon-icon" />
            <span><strong>{jobsData.length}</strong> Active Openings</span>
          </div>
          <span className="careers-hero-ribbon-dot">&bull;</span>
          <div className="careers-hero-ribbon-item">
            <FiLayers className="careers-hero-ribbon-icon" />
            <span><strong>{departments.length}</strong> Business Divisions</span>
          </div>
          <span className="careers-hero-ribbon-dot">&bull;</span>
          <div className="careers-hero-ribbon-item">
            <FiClock className="careers-hero-ribbon-icon" />
            <span><strong>&lt; 48h</strong> Direct Review</span>
          </div>
          <span className="careers-hero-ribbon-dot">&bull;</span>
          <div className="careers-hero-ribbon-item">
            <FiCheckCircle className="careers-hero-ribbon-icon" />
            <span><strong>100%</strong> Verified Roles</span>
          </div>
        </div>
      </section>

      {/* Results bar */}
      <div className="careers-results-bar">
        <div className="careers-results-count">
          Showing <strong>{filteredJobs.length}</strong> open position(s)
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>Reset Filters</Button>
        )}
      </div>

      {/* Job Cards or Empty State */}
      {filteredJobs.length > 0 ? (
        <div className="careers-grid">
          {filteredJobs.map((job) => {
            const posted = relativeTime(job.postedDate || job.createdAt || job.PostedDate || job.CreatedAt);
            return (
              <div key={job.id} className="careers-card" onClick={() => navigate(`/${job.id}?view=form`)}>
                <div className="careers-card-top">
                  <div className="careers-card-header">
                    <h3 className="careers-card-title">{job.title}</h3>
                    {job.type ? <span className="careers-tag careers-tag--accent">{job.type}</span> : null}
                  </div>
                  <div className="careers-card-company">{job.company}</div>

                  <div className="careers-card-meta-list">
                    <div className="careers-card-meta-item">
                      <FiMapPin className="careers-card-meta-icon" />
                      <span>{job.location}</span>
                    </div>
                    <div className="careers-card-meta-item">
                      <FiBriefcase className="careers-card-meta-icon" />
                      <span>{job.experience}</span>
                    </div>
                    {job.vacancies && (
                      <div className="careers-card-meta-item">
                        <FiUsers className="careers-card-meta-icon" />
                        <span>{job.vacancies} Position(s)</span>
                      </div>
                    )}
                    {posted && (
                      <div className="careers-card-meta-item careers-card-meta-item--posted">
                        <FiClock className="careers-card-meta-icon" />
                        <span>{posted}</span>
                      </div>
                    )}
                  </div>

                  <p className="careers-card-desc">
                    {job.description
                      ? job.description.length > 130
                        ? `${job.description.substring(0, 130)}...`
                        : job.description
                      : "No specific description provided."}
                  </p>
                </div>

                <div className="careers-card-footer">
                  <Button variant="primary" size="md" icon={<FiArrowRight />}
                    onClick={(e) => { e.stopPropagation(); navigate(`/${job.id}?view=form`); }}>
                    Apply Now
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <NoJobsState hasFilters={hasActiveFilters} onClear={clearFilters} />
      )}

      {/* Why Build Your Career With Us — Culture & Values Strip */}
      <section className="careers-culture-section">
        <div className="careers-culture-header">
          <span className="careers-tag careers-tag--primary">Why Join Us</span>
          <h2 className="careers-culture-title">Building High-Impact Careers in Healthcare &amp; Tech</h2>
          <p className="careers-culture-subtitle">
            Across GMS World, LDS and ViSole, we cultivate an environment where talent thrives and innovation shapes healthcare delivery across Pakistan.
          </p>
        </div>

        <div className="careers-culture-grid">
          <div className="careers-culture-card">
            <div className="careers-culture-icon-wrap">
              <FiAward className="careers-culture-icon" />
            </div>
            <h3 className="careers-culture-card-title">Continuous Growth &amp; Merit</h3>
            <p className="careers-culture-card-desc">
              Structured career roadmaps, continuous training, and fast-track promotions based purely on merit and performance.
            </p>
          </div>

          <div className="careers-culture-card">
            <div className="careers-culture-icon-wrap">
              <FiGlobe className="careers-culture-icon" />
            </div>
            <h3 className="careers-culture-card-title">National Healthcare Reach</h3>
            <p className="careers-culture-card-desc">
              Work on essential medical, diagnostic, and digital infrastructure that empowers healthcare providers across Pakistan.
            </p>
          </div>

          <div className="careers-culture-card">
            <div className="careers-culture-icon-wrap">
              <FiHeart className="careers-culture-icon" />
            </div>
            <h3 className="careers-culture-card-title">Culture of Purpose &amp; Belonging</h3>
            <p className="careers-culture-card-desc">
              Collaborative teams, equal opportunity workplace, supportive leadership, and competitive market compensation.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobOpenings;
